<template>
    <div>
      <v-data-table v-if="loading || results"
                    :headers="tableHeaders"
                    :items="resultsDisplay"
                    :items-per-page="itemsToShow"
                    :item-key="rowkey"
                    :loading="loading"
                    loading-text="Loading..."
                    :hide-default-footer="true"
                    :show-expand="showExpand"
                    :expanded.sync="expanded"
                    :sort-by="sortBy"
                    :sort-desc="sortDesc"
                    :single-expand="singleExpand"
                    :header-props="{sortIcon: 'fad fa-sort-up'}"
                    :footer-props="{
                        itemsPerPageOptions
                    }"
                    @update:options="handleOptionsUpdate">

        <template v-for="h in tableHeaders" v-slot:[`header.${h.value}`]="{header}">
          <slot :name="`header.${h.value}`" :header="header">
            <span :key="h.value">
              <span>{{header.text}}</span>
              <v-menu v-if="header.filterable"
                      offset-y
                      :close-on-content-click="false"
                      v-model="filtersOpen[header.value]"
                      :key="h.value">
                <template v-slot:activator="{on, attrs}">
                  <v-icon small v-bind="attrs" v-on="on"
                          :style="{'color': filters[header.value] ? 'black' : ''}">mdi-filter</v-icon>
                </template>
                <div style="background-color: white; width: 280px; padding: 20px;">
                  <div class="dfc">
                    <TextInput label="Search"
                               :clearable="true"
                               :autofocus="true"
                               @change="updateFilter(header.value, $event)"
                               @emitKeyup.enter="closeFilter(header.value)"></TextInput>
                  </div>
                </div>
              </v-menu>
              <v-tooltip v-if="header.icon && header.tooltipText" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" x-small icon>
                    <v-icon color="black" class="pl-1">
                      {{ header.icon }}
                    </v-icon>
                  </v-btn>
                </template>
                <span v-html="header.tooltipText"></span>
              </v-tooltip>
            </span>
          </slot>
        </template>

        <template v-slot:item="{ item, isExpanded, expand }">
          <tr data-testid="tableRow">
            <td v-if="showExpand">
              <v-btn icon
                     :class="shouldHideButton(item) ? 'hide-button' : ''"
                     data-testid="expandRowButton"
                     @click="expandIconClick(item, expand, isExpanded)">
                <v-icon>{{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </v-btn>
            </td>
            <td v-for="(h, i) in tableHeaders" :key="h.value">
              <slot :name="h.value" :item="item">
                <span v-html="item[h.value]" :key="i" />
              </slot>
            </td>
          </tr>
        </template>

        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length">
            <slot name="expansion-panel" :item="item" />
          </td>
        </template>

        <!-- Custom footer so the items-per-page select opens up instead of down, keeping the
             to prevent the menu from hiding behind the footer -->
        <template v-slot:footer="{ props, on }">
          <div v-if="!hideFooter" class="v-data-footer">
            <div class="v-data-footer__select">
              Rows per page:
              <v-select
                :value="props.options.itemsPerPage"
                :items="formatItemsPerPage(props.itemsPerPageOptions)"
                :menu-props="{ top: true, offsetY: true, contentClass: 'results-table-footer-menu' }"
                min-width="75px"
                hide-details
                @change="onItemsPerPageChange($event, props.options, on)">
              </v-select>
            </div>
            <div class="v-data-footer__pagination">
              {{ footerRangeText(props.pagination) }}
            </div>
            <div class="v-data-footer__icons-before">
              <v-btn icon
                     :disabled="props.pagination.page <= 1"
                     aria-label="Previous page"
                     @click="onPageChange(props.pagination.page - 1, props.options, on)">
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
            </div>
            <div class="v-data-footer__icons-after">
              <v-btn icon
                     :disabled="props.pagination.page >= props.pagination.pageCount"
                     aria-label="Next page"
                     @click="onPageChange(props.pagination.page + 1, props.options, on)">
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </div>
          </div>
        </template>

      </v-data-table>
    </div>
</template>

<script>
import Vue from 'vue';
import TextInput from '@/components/form/TextInput.vue';

export default {
    name: "ResultsTable",
    components: {
        TextInput
    },
    props: {
        tableHeaders: Array,
        results: Array,
        loading: {
            type: Boolean,
            default: false
        },
        hideFooter: {
            type: Boolean,
            default: false
        },
        hideButton: {
            type: [Boolean, Function],
            default: false
        },
        showExpand: {
            type: Boolean,
            default: false
        },
        singleExpand: {
            type: Boolean,
            default: false
        },
        rowkey: String,
        sortBy: String,
        sortDesc: {
          type: Boolean,
          default: false
        },
        itemsToShow: {
            type: Number,
            default: 10
        },
        // The rows-per-page choices; `itemsToShow` must be one of them or the table falls back to the first.
        itemsPerPageOptions: {
            type: Array,
            default: () => [10, 20, 50, 100, -1]
        },
        // Text added after the footer's total, e.g. ' loaded' when the total is only what has been fetched so far.
        footerCountSuffix: {
            type: String,
            default: ''
        }
    },
    emits: [
        'item-expanded',
        'update:options',
        'update:pagination'
    ],
    data () {
        return {
            resultsDisplay: [],
            filters: {},
            filtersOpen: {},
            expanded: []
        };
    },
    methods: {
        shouldHideButton (item) {
            if (typeof this.hideButton === 'function') {
                return this.hideButton(item);
            }
            return this.hideButton;
        },
        compile (template) {
            return Vue.compile(template);
        },
        updateFilter (field, value) {
            this.filters[field] = value;
            this.filterResults();
        },
        closeFilter (field) {
            this.filtersOpen[field] = false;
        },
        filterResults () {
          let data = [...this.results];
          let filters = Object.keys(this.filters);
            filters.forEach(f => {
              if (this.filters[f]) {
                    data = data.filter(d => (d[f] ?? '').toLowerCase().indexOf(this.filters[f].toLowerCase()) > -1);
                }
            });
            this.resultsDisplay = [...data];
        },
        rerender () {
            this.$nextTick(() => {
                this.$forceUpdate();
            });
        },
        expandIconClick (item, expand, isExpanded) {
            expand(!isExpanded);
            if (!isExpanded) {
                this.$emit('item-expanded', item);
            }
        },
        handleOptionsUpdate (options) {
            this.$emit('update:options', options);
            this.$emit('update:pagination', {
                page: options.page,
                itemsPerPage: options.itemsPerPage,
                filteredItems: this.resultsDisplay
            });
        },
        formatItemsPerPage (options) {
            return (options || []).map(o => (
                typeof o === 'object' && o !== null
                    ? o
                    : { text: o === -1 ? 'All' : String(o), value: o }
            ));
        },
        onItemsPerPageChange (itemsPerPage, options, on) {
            on['update:options']({ ...options, itemsPerPage, page: 1 });
        },
        onPageChange (page, options, on) {
            on['update:options']({ ...options, page });
        },
        footerRangeText (pagination) {
            const { pageStart, pageStop, itemsLength } = pagination;
            const start = itemsLength ? pageStart + 1 : 0;
            return `${start}-${pageStop} of ${itemsLength}${this.footerCountSuffix}`;
        }
    },
    watch: {
        results: {
            handler (newVal) {
                if (newVal) {
                  this.resultsDisplay = [...newVal];
                  this.filterResults();
                }
            },
            deep: true,
            immediate: true
        }
    },
    mounted () {
        this.tableHeaders.forEach((h) => {
            if (h.filterable) {
                this.filters[h.value] = '';
                this.filtersOpen[h.value] = false;
            }
        });
    }
};
</script>

<style scoped lang="scss">
@import '@/style.scss';

::v-deep .v-data-table-header__icon {
    opacity: 1;
    margin-left: 2px;
    transition: none!important;
}

::v-deep .v-data-table tr th,
::v-deep .v-data-table tr td {
    padding-top: 20px!important;
    padding-bottom: 20px!important;
}

::v-deep .v-data-table-header th {
  white-space: nowrap;
}

::v-deep .v-data-table tbody {
    tr:nth-child(2n + 1) {
        background-color: #F7FAFF;
    }
}

::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > td,
::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > th,
::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > td,
::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th,
::v-deep .v-data-table > .v-data-table__wrapper > table > tfoot > tr > td,
::v-deep .v-data-table > .v-data-table__wrapper > table > tfoot > tr > th {
    padding: 0 10px;
}

.hide-button {
    visibility: hidden;
}

.v-menu__content.theme--light.menuable__content__active {
    transform-origin: left top !important;
}

</style>
